import { useState } from 'react';

/**
 * Chips de motivo del gate anti-repetición. El operador marca qué falló (multi-
 * select) + un comentario libre opcional. El padre (ReferenceTray) decide qué
 * hacer con el resultado: guardar el motivo en el asset y/o mandar la corrección
 * al chat para regenerar. Presentacional puro — no llama a la API.
 *
 * Los chips se adaptan al tipo de asset: avatar/video suman labios/manos/movimiento;
 * audio tiene los suyos. Los labels son los que se guardan en `userRatingTags`.
 */

const CHIPS_BASE = [
  'Cara',
  'Ropa',
  'Encuadre',
  'Expresión',
  'Estilo/Color',
  'No siguió template',
  'Otro',
];

const CHIPS_TALKING = [
  'Cara',
  'Ropa',
  'Encuadre',
  'Expresión',
  'Labios (sync)',
  'Manos/gestos',
  'Movimiento',
  'No siguió template',
  'Otro',
];

const CHIPS_AUDIO = ['Voz', 'Tono', 'Pronunciación', 'Ritmo/velocidad', 'Otro'];

export function chipsForKind(kind?: string): string[] {
  if (kind === 'avatar' || kind === 'video') return CHIPS_TALKING;
  if (kind === 'audio') return CHIPS_AUDIO;
  return CHIPS_BASE;
}

interface RatingReasonChipsProps {
  kind?: string;
  submitting?: boolean;
  onConfirm: (tags: string[], comment: string) => void;
  onCancel?: () => void;
  confirmLabel?: string;
  title?: string;
}

export function RatingReasonChips({
  kind,
  submitting,
  onConfirm,
  onCancel,
  confirmLabel = 'Corregir y regenerar',
  title = '¿Qué hay que corregir?',
}: RatingReasonChipsProps) {
  const [tags, setTags] = useState<string[]>([]);
  const [comment, setComment] = useState('');
  const chips = chipsForKind(kind);
  const canConfirm = tags.length > 0 || comment.trim().length > 0;

  const toggle = (c: string) =>
    setTags((prev) => (prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c]));

  return (
    <div className="space-y-2 rounded-lg border border-slate-700 bg-slate-800/60 p-3">
      <div className="text-[11px] uppercase tracking-wide text-slate-400">{title}</div>
      <div className="flex flex-wrap gap-1.5">
        {chips.map((c) => {
          const on = tags.includes(c);
          return (
            <button
              key={c}
              type="button"
              onClick={() => toggle(c)}
              className={`rounded-full border px-2.5 py-1 text-xs transition-colors ${
                on
                  ? 'border-vision bg-vision-dark text-white'
                  : 'border-slate-600 bg-slate-900 text-slate-300 hover:border-slate-500'
              }`}
            >
              {c}
            </button>
          );
        })}
      </div>
      <textarea
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        placeholder="Detalle opcional (ej. la remera cambió de color)…"
        rows={2}
        className="w-full resize-none rounded-md border border-slate-700 bg-slate-950 px-2 py-1.5 text-xs text-slate-200 placeholder-slate-600 focus:border-vision focus:outline-none"
      />
      <div className="flex items-center gap-2">
        <button
          type="button"
          disabled={!canConfirm || submitting}
          onClick={() => onConfirm(tags, comment.trim())}
          className="rounded-md bg-vision-dark px-3 py-1.5 text-xs font-medium text-white hover:bg-vision disabled:cursor-not-allowed disabled:opacity-40"
        >
          {submitting ? 'Enviando…' : confirmLabel}
        </button>
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="text-xs text-slate-500 hover:text-slate-300"
          >
            Cancelar
          </button>
        )}
        {!canConfirm && (
          <span className="text-[10px] text-slate-500">Elegí al menos un motivo o escribí el detalle</span>
        )}
      </div>
    </div>
  );
}
